import { useEffect, useMemo, useState } from 'react'

// Live countdown to a target time (ISO string or ms). A `now` clock ticks once a
// second (and stops itself at expiry); `remaining` is DERIVED in render, so the
// effect only sets state from the interval callback (never synchronously).
// Returns { remaining, isExpired, label }:
//   remaining  ms left (null when there is no target)
//   isExpired  true once the target has passed
//   label      "2D 06H 46M 30S" — leading zero-value units are dropped
const pad = (n) => String(n).padStart(2, '0')

const formatDuration = (ms) => {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const d = Math.floor(totalSec / 86400)
  const h = Math.floor((totalSec % 86400) / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  // Build "Dd Hh Mm Ss", dropping leading units that are zero (e.g. under an
  // hour → "06M 30S") but always keeping seconds so it visibly ticks.
  const parts = []
  if (d > 0) parts.push(`${d}D`)
  if (d > 0 || h > 0) parts.push(`${pad(h)}H`)
  if (d > 0 || h > 0 || m > 0) parts.push(`${pad(m)}M`)
  parts.push(`${pad(s)}S`)
  return parts.join(' ')
}

export const useCountdown = (target) => {
  const targetMs = useMemo(() => {
    if (!target) return null
    const t = new Date(target).getTime()
    return Number.isNaN(t) ? null : t
  }, [target])

  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (targetMs == null || targetMs <= Date.now()) return // no target or already expired
    const id = setInterval(() => {
      setNow(Date.now())
      if (Date.now() >= targetMs) clearInterval(id) // stop once expired
    }, 1000)
    return () => clearInterval(id)
  }, [targetMs])

  const remaining = targetMs == null ? null : Math.max(0, targetMs - now)
  const isExpired = remaining != null && remaining <= 0
  const label = remaining == null ? '' : formatDuration(remaining)
  return { remaining, isExpired, label }
}
